import { Fragment, useMemo } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import type { IInformationItemProps } from "@components/molecules/informationItem";
import { VInformationItemVariants } from "@components/molecules/informationItem/variant";
import { Avatar } from "@components/atoms/avatar";
import { addColon } from "@utils";
import { Link } from "@components/atoms/link";
import { Typography } from "@components/atoms/typography";
import { Label } from "@components/atoms/label";

/**
 * InformationItem component displays information about an entity.
 *
 * @param {IInformationItemProps} props - The props for the InformationItem component.
 * @param {string} [props.id] - The unique identifier of the entity.
 * @param {string} [props.name] - The first name of the entity.
 * @param {string} [props.lastName] - The last name of the entity.
 * @param {string} [props.avatarUrl] - The URL of the avatar image.
 * @param {boolean} [props.noAvatar] - Flag to indicate if the avatar should not be displayed.
 * @param {string} [props.link] - The link associated with the entity.
 * @param {string} [props.title] - The title associated with the item.
 * @param {{ [key: string]: string }} [props.details] - Details about the entity, represented as key-value pairs.
 * @param {string} [props.variant] - The variant of the InformationItem.
 * @returns InformationItem The rendered InformationItem component.
 */
export function InformationItem(props: IInformationItemProps) {
  const { t } = useTranslation("common");

  const variantProps = VInformationItemVariants(t)[props?.variant];

  const isDoctor = props?.variant === "doctor";
  const doctorPrefix = isDoctor ? `${t("doctor")} ` : "";

  const id = props?.id || variantProps?.id;
  const firstName = doctorPrefix + (props?.name || variantProps?.name || "");
  const lastName = props?.lastName || variantProps?.lastName || "";
  const avatarUrl = props?.avatarUrl || variantProps?.avatarUrl;
  const noAvatar = props?.noAvatar || variantProps?.noAvatar;
  const link = props?.link || variantProps?.link || "";
  const title = props?.title || variantProps?.title;
  const details = props?.details || variantProps?.details;

  const renderedDetails = useMemo(() => {
    const detailsKey = Object.keys(details) || [];

    return detailsKey?.map((key, index) => {
      const detail = details[key];

      return (
        <Fragment key={detail}>
          <Typography fontFamily={"Morabba"} variant={"caption1"}>
            {index !== 0 && "/"}
          </Typography>
          <Typography fontFamily={"Morabba"} variant={"caption1"}>
            {detail}
          </Typography>
        </Fragment>
      );
    });
  }, [details]);

  return (
    <div className={clsx(classes.informationItem)}>
      {!noAvatar && (
        <div className={clsx(classes.informationItem__column)}>
          <Avatar
            userInfo={{ firstName, lastName, imageUrl: avatarUrl }}
            size={"small"}
            variant={60}
          />
          {link && <Link to={link}>{"مشاهده پروفایل"}</Link>}
        </div>
      )}
      <div className={clsx(classes.informationItem__column)}>
        <div>
          <Typography fontFamily={"Morabba"} variant={"caption1"}>
            {addColon(title)}
          </Typography>
        </div>
        <div className={clsx(classes.informationItem__texts)}>
          <div>
            <Label text={id} variant={props?.variant} />
          </div>
          <div>
            <Typography
              variant={"subtitle1"}
            >{`${firstName} ${lastName}`}</Typography>
          </div>
        </div>
        <div className={clsx(classes.informationItem__details)}>
          {renderedDetails}
        </div>
      </div>
    </div>
  );
}
